<template>
  <div class="cf-flex cf-flex--column cf-flex--gap-12">
    <div
      v-if="isInitializing"
      class="cf-section-loader-container"
    >
      <div class="cf-loader" />
    </div>

    <p
      v-else-if="failedToInitialize"
      class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy cf-subtitle--brand-primary"
    >
      Oops! Our payment form failed to load. Please try again later.
    </p>

    <template v-else>
      <cf-text-input
        id="frmNameCC"
        v-model="nameOnCard"
        type="text"
        label="Name on Card"
        name="ccname"
        autocomplete="cc-name"
        :validations="{
          full_name: true,
          required: !skipRequiredValidation
        }"
        @focus="$emit('interacted')"
      />
    </template>

    <div id="sq-card" />
  </div>
</template>



<script>
  import errorHandlers from '@/services/error-handlers';
  import events from '@/services/events';

  import CfTextInput from '@/view/components/shared/cf-text-input/index.vue';

  export default {
    name: 'CfSquareCreditCardForm',

    components: {
      CfTextInput
    },

    inject: {
      $validator: {
        from: '$validator'
      }
    },

    props: {
      paymentGateway: {
        type: Object,
        required: true
      },

      skipRequiredValidation: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        failedToInitialize: false,
        isInitializing: true,
        nameOnCard: null,
        squarePayment: null,
        squareCard: null
      };
    },

    unmounted() {
      this.onDestroyed();
    },

    mounted() {
      this.onMounted();
    },

    methods: {
      onDestroyed() {
        if (this.squareCard) this.squareCard.destroy();
      },

      onMounted() {
        this.initializeSquareForm();
      },

      async initializeSquareForm() {
        try {
          this.squarePayment = window.Square.payments(
            this.paymentGateway.providers.Square.applicationId,
            this.paymentGateway.paymentLocationId
          );

          this.squareCard = await this.squarePayment.card({
            style: {
              '.input-container.is-focus': {
                borderColor: '#b1b1b1',
                borderWidth: '1px'
              }
            }
          });

          this.squareCard.addEventListener('focusClassAdded', this.handleSquareCardFormFocus);

          await this.squareCard.attach('#sq-card');
        }

        catch (error) {
          this.failedToInitialize = true;
          errorHandlers.silent(error);
        }

        finally {
          this.isInitializing = false;
        }
      },

      handleSquareCardFormFocus() {
        this.$emit('interacted');
      },

      handleSubmitSuccess({ details, token }) {
        this.$emit('success', {
          paymentMethod: {
            type: 'third-party-form',
            body: {
              billingAddress: {
                postalCode: details.billing.postalCode
              },
              cardNumberLastFour: details.card.last4,
              cardType: details.card.brand,
              expiration: {
                month: details.card.expMonth,
                year: details.card.expYear
              },
              nameOnCard: this.nameOnCard,
              paymentType: 'CreditCard',
              token
            }
          }
        });
      },

      handleSubmitFailure(errors) {
        const [tokenizationError] = errors;

        if (tokenizationError.type === 'VALIDATION_ERROR') {
          events.emit('notification', {
            type: 'error',
            message: 'Invalid payment details. Please verify and try again.'
          });
        }

        else {
          errorHandlers.generic(tokenizationError);
        }

        this.$emit('error', tokenizationError);
      },

      async submit() {
        try {
          const { details, errors, status, token } = await this.squareCard.tokenize();

          if (status === 'OK') this.handleSubmitSuccess({ details, token });
          else if (status === 'INVALID') this.handleSubmitFailure(errors);
        }

        catch (error) {
          this.$emit('error', error);
        }
      }
    }
  };
</script>


<style lang="sass">
  .sq-card-iframe-container
    color: var(--color-dark)
    background: var(--color-light)
    border: 1px solid var(--color-light-gray)
    box-shadow: var(--shadow-standard)
    border-radius: 1000px
    transition: border-color 150ms

    &:hover
      border: 1px solid var(--color-gray) !important
</style>
